import { isStaticCartDrawerOpen } from "./is-static-cart-drawer-open";
import { getShopifyCartUrl } from "./shopify-cart-url";

/** Section ids most Dawn-style themes expect on cart Ajax responses. */
const DEFAULT_CART_SECTIONS = "cart-drawer,cart-icon-bubble,cart-drawer-section";

export function resolveCartSectionsToRequest() {
  if (typeof document === "undefined") return DEFAULT_CART_SECTIONS;

  const cartObject =
    document.querySelector("cart-drawer") ||
    document.querySelector("cart-notification") ||
    document.querySelector("cart-drawer-component");

  if (cartObject && typeof cartObject.getSectionsToRender === "function") {
    try {
      const sectionIds = cartObject
        .getSectionsToRender()
        .map((section) => section?.id || section?.section)
        .filter(Boolean);
      if (sectionIds.length) return sectionIds.join(",");
    } catch (_) {}
  }

  return DEFAULT_CART_SECTIONS;
}

function getSectionInnerHtml(html, selector) {
  if (!html || typeof html !== "string") return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    if (selector) {
      const match = doc.querySelector(selector);
      if (match) return match.innerHTML;
    }
    const shopifySection = doc.querySelector(".shopify-section");
    if (shopifySection) return shopifySection.innerHTML;
    return doc.body?.innerHTML || html;
  } catch (_) {
    return html;
  }
}

function applySectionHtmlFallback(sections = {}) {
  if (typeof document === "undefined" || !sections) return;

  Object.entries(sections).forEach(([sectionKey, html]) => {
    if (!html) return;

    const candidates = [
      document.getElementById(sectionKey),
      document.getElementById(`shopify-section-${sectionKey}`),
      document.querySelector(`[data-section-id="${sectionKey}"]`),
    ].filter(Boolean);

    candidates.forEach((element) => {
      const nextHtml = getSectionInnerHtml(html);
      if (nextHtml) element.innerHTML = nextHtml;
    });
  });
}

function applyThemeCartDrawerSections(parsedState) {
  if (!parsedState?.sections || typeof document === "undefined") return false;

  const cartObject =
    document.querySelector("cart-drawer") ||
    document.querySelector("cart-notification");

  if (cartObject && typeof cartObject.renderContents === "function") {
    try {
      cartObject.classList.remove("is-empty");
      cartObject.renderContents(parsedState);
      return true;
    } catch (error) {
      console.warn("[WeUpsell] Theme cart drawer renderContents failed:", error);
    }
  }

  if (cartObject && typeof cartObject.getSectionsToRender === "function") {
    try {
      cartObject.getSectionsToRender().forEach((section) => {
        const sectionHtml = parsedState.sections[section.section || section.id];
        if (!sectionHtml) return;
        const target =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);
        if (!target) return;
        target.innerHTML = getSectionInnerHtml(sectionHtml, section.selector);
      });
      cartObject.classList?.remove("is-empty");
      return true;
    } catch (error) {
      console.warn("[WeUpsell] Theme cart drawer section patch failed:", error);
    }
  }

  applySectionHtmlFallback(parsedState.sections);
  return false;
}

/**
 * Horizon `cart-icon` listens for `cart:update` (not `cart:updated`) and treats
 * `source: "product-form-component"` as a relative increment. Always pass an
 * absolute itemCount with a non-product-form source so empty carts clear the bubble.
 */
function updateHorizonCartIconBubble(itemCount) {
  if (typeof document === "undefined") return;
  const count = Math.max(0, Number(itemCount) || 0);

  document.querySelectorAll("cart-icon").forEach((icon) => {
    try {
      if (typeof icon.renderCartBubble === "function") {
        // second arg false => set absolute count (not increment)
        icon.renderCartBubble(count, false, count > 0);
        return;
      }
      if ("currentCartCount" in icon) {
        icon.currentCartCount = count;
      }
      icon.classList?.toggle?.("header-actions__cart-icon--has-cart", count > 0);
      const bubble = icon.querySelector?.(".cart-bubble, [ref='cartBubble']");
      const countEl = icon.querySelector?.(
        ".cart-bubble__text-count, [ref='cartBubbleCount']"
      );
      if (bubble) {
        bubble.classList.toggle("visually-hidden", count === 0);
      }
      if (countEl) {
        countEl.textContent = String(count);
        countEl.classList.toggle("hidden", count === 0);
      }
    } catch (_) {}
  });

  try {
    sessionStorage.setItem(
      "cart-count",
      JSON.stringify({ value: String(count), timestamp: Date.now() })
    );
  } catch (_) {}
}

function dispatchThemeCartEvents(cartSnapshot) {
  if (typeof document === "undefined") return;

  const itemCount = Math.max(0, Number(cartSnapshot?.item_count) || 0);
  const detail = cartSnapshot ? { cart: cartSnapshot } : undefined;

  ["cart:updated", "cart:refresh", "theme:cart:change"].forEach((eventName) => {
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent(eventName, { bubbles: true, detail })
      );
    } catch (_) {}
  });

  // Horizon / OS 2.0 cart-icon + cart-drawer-component
  try {
    const horizonDetail = {
      data: {
        itemCount,
        source: "weupsell",
        ...(cartSnapshot ? { cart: cartSnapshot } : {}),
      },
      cart: cartSnapshot,
    };
    document.dispatchEvent(
      new CustomEvent("cart:update", { bubbles: true, detail: horizonDetail })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:update", { bubbles: true, detail: horizonDetail })
    );
  } catch (_) {}

  updateHorizonCartIconBubble(itemCount);

  try {
    if (typeof window.publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: "weupsell",
        cartData: cartSnapshot,
        itemCount,
      });
    }
  } catch (_) {}
}

async function fetchCartSnapshot() {
  const res = await fetch(getShopifyCartUrl("cart.js"));
  if (!res.ok) throw new Error("Failed to fetch cart");
  return res.json();
}

function refreshWeupsellStaticCartDrawer(cartSnapshot) {
  if (typeof window === "undefined") return;

  try {
    if (isStaticCartDrawerOpen()) {
      window.dispatchEvent(
        new CustomEvent("weupsell:static-cart-drawer-cart-updated", {
          detail: cartSnapshot ? { cart: cartSnapshot } : undefined,
        })
      );
      return;
    }
    window.dispatchEvent(new CustomEvent("weupsell:static-cart-drawer-refresh"));
  } catch (_) {}
}

/**
 * Refresh theme cart UI (icon bubble, drawer sections) + WeUpsell static drawer.
 * Safe after add.js / change.js / update.js — Horizon needs absolute itemCount.
 * @param {object|null} cartOrSectionsResult - Response that may include `sections`, or a cart.js snapshot.
 * @param {{ fetchCart?: boolean, refreshDrawer?: boolean, cartSnapshot?: object|null }} options
 */
export async function syncStorefrontCartUi(cartOrSectionsResult = null, options = {}) {
  if (typeof window === "undefined") return null;

  const shouldFetchCart = options.fetchCart !== false;
  const shouldRefreshDrawer = options.refreshDrawer !== false;
  let cartSnapshot =
    options.cartSnapshot && typeof options.cartSnapshot === "object"
      ? options.cartSnapshot
      : null;

  if (cartOrSectionsResult?.sections) {
    applyThemeCartDrawerSections(cartOrSectionsResult);
  }

  if (!cartSnapshot && cartOrSectionsResult && Array.isArray(cartOrSectionsResult.items)) {
    cartSnapshot = cartOrSectionsResult;
  }

  if (shouldFetchCart) {
    try {
      cartSnapshot = await fetchCartSnapshot();
    } catch (_) {}
  }

  dispatchThemeCartEvents(cartSnapshot);
  if (shouldRefreshDrawer) {
    refreshWeupsellStaticCartDrawer(cartSnapshot);
  }

  return cartSnapshot;
}

/**
 * After a successful /cart/add.js call, refresh theme cart UI and WeUpsell static drawer.
 * @param {object} addResult - Parsed JSON from cart/add.js (may include `sections`).
 * @param {{ fetchCart?: boolean }} options
 */
export async function syncStorefrontCartAfterAdd(addResult, options = {}) {
  if (typeof window === "undefined" || !addResult) return null;
  return syncStorefrontCartUi(addResult, options);
}

export function exposeSyncStorefrontCartAfterAdd() {
  if (typeof window !== "undefined") {
    window.__WEUPSELL_SYNC_CART_AFTER_ADD__ = syncStorefrontCartAfterAdd;
    window.__WEUPSELL_SYNC_CART_UI__ = syncStorefrontCartUi;
  }
}
